import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { Link, Navigate } from 'react-router-dom';
import { PostComposer } from '../components/posts/PostComposer';
import { PostCard } from '../components/posts/PostCard';
import { FeedSkeleton } from '../components/LoadingSkeleton';
import { getFeed } from '../services/posts';
import { getArchivedEvents, getUpcomingEvents } from '../services/events';
import EventCard from '../components/events/EventCard';
import { useEffect, useRef, useState } from 'react';

export function FeedPage() {
  const { isAuthenticated, user } = useAuth();
  const observerRef = useRef<HTMLDivElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const [showArchive, setShowArchive] = useState(false);
  const [showComposer, setShowComposer] = useState(false);

  const handlePostCreated = () => {
    setShowComposer(false);
    setTimeout(() => {
      feedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Fetch upcoming events
  const { data: upcomingEvents = [] } = useQuery({
    queryKey: ['events'],
    queryFn: getUpcomingEvents,
    enabled: isAuthenticated,
  });

  const { data: archivedEvents = [] } = useQuery({
    queryKey: ['events', 'archived'],
    queryFn: getArchivedEvents,
    enabled: isAuthenticated,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ['posts'],
    queryFn: ({ pageParam = 1 }) => getFeed(pageParam, 20, null),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasMore
        ? lastPage.pagination.page + 1
        : undefined;
    },
    initialPageParam: 1,
    enabled: isAuthenticated,
  });

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = observerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="lg:col-span-1">
            <FeedSkeleton />
          </div>
          <div className="lg:col-span-1">
            <FeedSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">
            Kunne ikke indlæse opslag: {error?.message || 'Ukendt fejl'}
          </p>
        </div>
      </div>
    );
  }

  const posts = data?.pages.flatMap((page) => page.posts) ?? [];

  // Separate posts: active vs archived/expired
  // Active: not manually archived AND not past expiresAt
  const now = new Date();

  const activePosts = posts.filter((post) => {
    if (post.archivedAt) return false;
    if (post.expiresAt && new Date(post.expiresAt) <= now) return false;
    return true;
  });

  const archivedPosts = posts.filter((post) => {
    return Boolean(post.archivedAt) || (post.expiresAt ? new Date(post.expiresAt) <= now : false);
  });

  // Show all upcoming events, soonest first
  const frontpageEvents = [...upcomingEvents].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-wrap justify-end sm:justify-start gap-2 mb-6">
        <Link
          to="/events/create"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors no-underline"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>Nyt event</span>
        </Link>
        <button
          type="button"
          onClick={() => {
            setShowComposer(true);
            document.getElementById('seneste-opslag')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
          <span>Nyt opslag</span>
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Events */}
        <div className="lg:col-span-1">
          {/* Upcoming Events Section */}
          {frontpageEvents.length > 0 && (
            <div className="mb-8">
              <div className="space-y-3">
                {frontpageEvents.map((event) => (
                  <EventCard key={event.id} event={event} expanded currentUserId={user?.id} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Posts */}
        <div className="lg:col-span-1">
          {/* Active Posts Feed */}
          <div className="mb-8" id="seneste-opslag" ref={feedRef}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Opslag</h2>
              <button
                onClick={() => setShowComposer(!showComposer)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
              >
                {showComposer ? 'Luk' : 'Opret nyt opslag'}
              </button>
            </div>

            {/* Post composer */}
            {showComposer && (
              <div className="mb-4">
                <PostComposer onPostCreated={handlePostCreated} />
              </div>
            )}

            {activePosts.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-600">
                  Ingen aktive opslag. Vær den første til at dele noget!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {activePosts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            )}
          </div>

          {/* Archive Section */}
          {(archivedEvents.length > 0 || archivedPosts.length > 0) && (
            <div className="mb-8">
              <button
                onClick={() => setShowArchive(!showArchive)}
                className="flex items-center justify-between w-full bg-gray-100 hover:bg-gray-200 rounded-lg p-4 transition-colors mb-4"
              >
                <h2 className="text-sm font-bold text-gray-700">
                  Arkiv ({archivedEvents.length + archivedPosts.length})
                </h2>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${showArchive ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showArchive && (
                <div className="space-y-4">
                  {archivedEvents.map((event) => (
                    <EventCard key={event.id} event={event} currentUserId={user?.id} />
                  ))}
                  {archivedPosts.map((post) => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Loading indicator for next page */}
          {isFetchingNextPage && (
            <div className="bg-white rounded-lg shadow p-4 animate-pulse">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/6"></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          )}

          {/* Observer target for infinite scroll */}
          <div ref={observerRef} className="h-4" />

          {/* End of feed message */}
          {!hasNextPage && posts.length > 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">Du har set alle opslag</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
